import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { BASE_URL } from '../config';
import { useAuth } from '../context/AuthContext';

interface Utente {
  id: number;
  username: string;
  password?: string;
  nome: string;
  cognome: string;
  enabled: boolean;
  strutture?: string[];
}

interface Struttura {
  nome: string;
}

const UtentiPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [utenti, setUtenti] = useState<Utente[]>([]);
  const [strutture, setStrutture] = useState<Struttura[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUtente, setEditingUtente] = useState<Utente | null>(null);
  const [formData, setFormData] = useState<Partial<Utente>>({ enabled: true, strutture: [] });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadUtenti();
    loadStrutture();
  }, []);

  const loadUtenti = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/utenti`, { headers });
      if (!response.ok) {
        throw new Error('Errore nel caricamento utenti');
      }
      const data = await response.json();

      // Carica strutture per ogni utente
      const utentiConStrutture = await Promise.all(
        data.map(async (utente: Utente) => {
          const detailResponse = await fetch(`${BASE_URL}/utenti/${utente.id}`, { headers });
          if (!detailResponse.ok) return { ...utente, strutture: [] };
          const detail = await detailResponse.json();
          return { ...utente, strutture: detail.strutture || [] };
        })
      );

      setUtenti(utentiConStrutture);
    } catch (err) {
      console.error('Error loading utenti:', err);
      setError('Errore nel caricamento degli utenti');
    }
  };

  const loadStrutture = async () => {
    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/strutture`, { headers });
      if (!response.ok) {
        console.error('Failed to load strutture');
        setStrutture([]);
        return;
      }
      const data = await response.json();
      setStrutture(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading strutture:', err);
      setStrutture([]);
    }
  };

  const handleOpenDialog = (utente?: Utente) => {
    if (utente) {
      setEditingUtente(utente);
      setFormData({ ...utente, password: '' }); // Non mostrare password
    } else {
      setEditingUtente(null);
      setFormData({ enabled: true, strutture: [] });
    }
    setOpenDialog(true);
    setError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUtente(null);
    setFormData({ enabled: true, strutture: [] });
    setError(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.username) {
        setError('Username obbligatorio');
        return;
      }

      if (!editingUtente && !formData.password) {
        setError('Password obbligatoria per nuovo utente');
        return;
      }

      const payload = { ...formData };

      // Se editing e password vuota, non inviarla
      if (editingUtente && !formData.password) {
        delete payload.password;
      }

      const url = editingUtente
        ? `${BASE_URL}/utenti/${editingUtente.id}`
        : `${BASE_URL}/utenti`;

      const method = editingUtente ? 'PUT' : 'POST';

      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nel salvataggio');
      }

      loadUtenti();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (id: number, username: string) => {
    if (!window.confirm(`Eliminare l'utente "${username}"?`)) return;

    try {
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (user?.id) {
        headers['x-user-id'] = user.id.toString();
      }

      const response = await fetch(`${BASE_URL}/utenti/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Errore nell\'eliminazione');
      }

      loadUtenti();
    } catch (err: any) {
      alert(err.message || 'Errore nell\'eliminazione');
    }
  };

  const handleStruttureChange = (event: any) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      strutture: typeof value === 'string' ? value.split(',') : value,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Gestione Utenti
        </Typography>
        {hasPermission('utenti', 'create') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Nuovo Utente
          </Button>
        )}
      </Box>

      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cognome</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Strutture</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Stato</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {utenti.map((utente) => (
              <TableRow key={utente.id} hover>
                <TableCell>{utente.username}</TableCell>
                <TableCell>{utente.nome || '-'}</TableCell>
                <TableCell>{utente.cognome || '-'}</TableCell>
                <TableCell>
                  {utente.strutture && utente.strutture.length > 0 ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {utente.strutture.map((s) => (
                        <Chip key={s} label={s} size="small" />
                      ))}
                    </Box>
                  ) : (
                    <Chip label="Tutte" size="small" color="primary" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={utente.enabled ? 'Attivo' : 'Disabilitato'}
                    color={utente.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {hasPermission('utenti', 'update') && (
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(utente)}
                    >
                      <Edit />
                    </IconButton>
                  )}
                  {hasPermission('utenti', 'delete') && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(utente.id, utente.username)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog per creazione/modifica */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUtente ? 'Modifica Utente' : 'Nuovo Utente'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            type="text"
            fullWidth
            required
            value={formData.username || ''}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label={editingUtente ? 'Password (lascia vuoto per non modificare)' : 'Password'}
            type="password"
            fullWidth
            required={!editingUtente}
            value={formData.password || ''}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Nome"
            type="text"
            fullWidth
            value={formData.nome || ''}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Cognome"
            type="text"
            fullWidth
            value={formData.cognome || ''}
            onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
            sx={{ mt: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel>Strutture Visibili</InputLabel>
            <Select
              multiple
              value={formData.strutture || []}
              onChange={handleStruttureChange}
              input={<OutlinedInput label="Strutture Visibili" />}
              renderValue={(selected) => {
                if (selected.length === 0) return 'Tutte le strutture';
                return `${selected.length} strutture`;
              }}
            >
              {strutture.map((struttura) => (
                <MenuItem key={struttura.nome} value={struttura.nome}>
                  <Checkbox checked={(formData.strutture || []).indexOf(struttura.nome) > -1} />
                  <ListItemText primary={struttura.nome} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.enabled !== false}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              />
            }
            label="Utente attivo"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UtentiPage;
